import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchReadingMilestone,
  createReadingMilestone,
  updateReadingMilestone,
  deleteReadingMilestone,
} from "@/api/reading-milestones";
import { fetchPromotions } from "@/api/subscription-plans";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import AdminLayout from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Save, Trash2 } from "lucide-react";
import type {
  ReadingMilestoneInput,
  TimeSource,
} from "@/types/readingMilestones";
import MilestoneAchieversTable from "@/components/reading-rewards/MilestoneAchieversTable";

const ReadingMilestoneForm = () => {
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [thresholdMinutes, setThresholdMinutes] = useState<number>(60);
  const [thresholdHours, setThresholdHours] = useState<string>("1");
  const [thresholdMins, setThresholdMins] = useState<string>("0");
  const [timeSource, setTimeSource] = useState<TimeSource>("either");
  const [promotionId, setPromotionId] = useState<string>("");
  const [notificationTemplateId, setNotificationTemplateId] =
    useState<string>("");
  const [rewardMessage, setRewardMessage] = useState("");
  const [badgeIconUrl, setBadgeIconUrl] = useState("");
  const [sortOrder, setSortOrder] = useState<number>(0);
  const [isActive, setIsActive] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Fetch existing milestone for edit mode
  const { data: milestone, isLoading: isLoadingMilestone } = useQuery({
    queryKey: ["readingMilestone", Number(id)],
    queryFn: () => fetchReadingMilestone(Number(id)),
    enabled: isEditMode,
  });

  // Fetch promotions for dropdown
  const { data: promotions = [] } = useQuery({
    queryKey: ["activePromoDiscounts"],
    queryFn: fetchPromotions,
  });

  // Fetch notification templates for dropdown
  const { data: notificationTemplates = [] } = useQuery({
    queryKey: ["notificationTemplates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notification_templates")
        .select("id, name")
        .eq("is_active", true)
        .order("name");
      if (error) throw error;
      return data || [];
    },
  });

  // Populate form when milestone data loads
  useEffect(() => {
    if (milestone) {
      setName(milestone.name);
      setDescription(milestone.description || "");
      setThresholdMinutes(milestone.thresholdMinutes);
      setThresholdHours(
        String(Math.floor(milestone.thresholdMinutes / 60))
      );
      setThresholdMins(String(milestone.thresholdMinutes % 60));
      setTimeSource(milestone.timeSource);
      setPromotionId(
        milestone.promotionId ? String(milestone.promotionId) : ""
      );
      setNotificationTemplateId(
        milestone.notificationTemplateId
          ? String(milestone.notificationTemplateId)
          : ""
      );
      setRewardMessage(milestone.rewardMessage || "");
      setBadgeIconUrl(milestone.badgeIconUrl || "");
      setSortOrder(milestone.sortOrder);
      setIsActive(milestone.isActive);
    }
  }, [milestone]);

  // Sync threshold when hours/mins change
  useEffect(() => {
    const h = parseInt(thresholdHours, 10) || 0;
    const m = parseInt(thresholdMins, 10) || 0;
    setThresholdMinutes(h * 60 + m);
  }, [thresholdHours, thresholdMins]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: createReadingMilestone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["readingMilestones"] });
      toast({
        title: "Milestone created",
        description: "The reading milestone has been created successfully.",
      });
      navigate("/admin/reading-rewards");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: Partial<ReadingMilestoneInput>) =>
      updateReadingMilestone(Number(id), input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["readingMilestones"] });
      queryClient.invalidateQueries({
        queryKey: ["readingMilestone", Number(id)],
      });
      toast({
        title: "Milestone updated",
        description: "The reading milestone has been updated successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteReadingMilestone,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["readingMilestones"] });
      toast({
        title: "Milestone deleted",
        description: "The reading milestone has been deleted successfully.",
      });
      navigate("/admin/reading-rewards");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    if (!name.trim()) {
      toast({
        title: "Validation error",
        description: "Name is required.",
        variant: "destructive",
      });
      return;
    }
    if (thresholdMinutes <= 0) {
      toast({
        title: "Validation error",
        description: "Threshold must be greater than 0.",
        variant: "destructive",
      });
      return;
    }

    const input: ReadingMilestoneInput = {
      name: name.trim(),
      description: description.trim() || null,
      thresholdMinutes,
      timeSource,
      promotionId: promotionId && promotionId !== "none" ? Number(promotionId) : null,
      notificationTemplateId: notificationTemplateId && notificationTemplateId !== "none"
        ? Number(notificationTemplateId)
        : null,
      rewardMessage: rewardMessage.trim() || null,
      badgeIconUrl: badgeIconUrl.trim() || null,
      sortOrder,
      isActive,
      createdBy: user?.id ?? null,
    };

    if (isEditMode) {
      updateMutation.mutate(input);
    } else {
      createMutation.mutate(input);
    }
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  if (isEditMode && isLoadingMilestone) {
    return (
      <AdminLayout title="Loading...">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </AdminLayout>
    );
  }

  const pageTitle = isEditMode ? `Edit: ${milestone?.name ?? ""}` : "New Reading Milestone";

  return (
    <AdminLayout title={pageTitle}>
      <div className="mb-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/reading-rewards")}
          className="gap-1"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Reading Rewards
        </Button>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className={`w-full grid h-10 ${isEditMode ? "grid-cols-2" : "grid-cols-1"}`}>
          <TabsTrigger value="details" className="text-sm">
            Details
          </TabsTrigger>
          {isEditMode && (
            <TabsTrigger value="achievers" className="text-sm">
              Achievers
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="details" className="mt-4 space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Basic Information</CardTitle>
              <CardDescription>
                Define the milestone name, threshold, and time source.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder='e.g. "1 Hour Reader"'
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Admin-facing description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Threshold *</Label>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min={0}
                      className="w-20"
                      value={thresholdHours}
                      onChange={(e) => setThresholdHours(e.target.value)}
                    />
                    <span className="text-sm text-muted-foreground">hours</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Input
                      type="number"
                      min={0}
                      max={59}
                      className="w-20"
                      value={thresholdMins}
                      onChange={(e) => setThresholdMins(e.target.value)}
                    />
                    <span className="text-sm text-muted-foreground">min</span>
                  </div>
                  <span className="text-xs text-muted-foreground ml-2">
                    = {thresholdMinutes} total minutes
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="timeSource">Time Source</Label>
                <Select
                  value={timeSource}
                  onValueChange={(v) => setTimeSource(v as TimeSource)}
                >
                  <SelectTrigger id="timeSource">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="either">
                      Either (reading OR listening independently)
                    </SelectItem>
                    <SelectItem value="reading">
                      Reading only (book_reads)
                    </SelectItem>
                    <SelectItem value="listening">
                      Listening only (audio_plays)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-3">
                <Switch
                  id="isActive"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
                <Label htmlFor="isActive">Active</Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sortOrder">Sort Order</Label>
                <Input
                  id="sortOrder"
                  type="number"
                  min={0}
                  className="w-24"
                  value={sortOrder}
                  onChange={(e) =>
                    setSortOrder(parseInt(e.target.value, 10) || 0)
                  }
                />
              </div>
            </CardContent>
          </Card>

          {/* Reward Configuration */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Reward Configuration</CardTitle>
              <CardDescription>
                Link a promotion and notification template to this milestone.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="promotionId">Linked Promotion</Label>
                <Select value={promotionId} onValueChange={setPromotionId}>
                  <SelectTrigger id="promotionId">
                    <SelectValue placeholder="Select a promotion (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {promotions.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name}{" "}
                        {p.discountType === "percentage"
                          ? `(${p.discountValue}% off)`
                          : `(₹${p.discountValue} off)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notificationTemplateId">
                  Notification Template
                </Label>
                <Select
                  value={notificationTemplateId}
                  onValueChange={setNotificationTemplateId}
                >
                  <SelectTrigger id="notificationTemplateId">
                    <SelectValue placeholder="Select a template (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {notificationTemplates.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="rewardMessage">
                  In-App Reward Message
                </Label>
                <Textarea
                  id="rewardMessage"
                  placeholder="Congratulations! You've unlocked a special reward..."
                  value={rewardMessage}
                  onChange={(e) => setRewardMessage(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="badgeIconUrl">Badge Icon URL</Label>
                <Input
                  id="badgeIconUrl"
                  placeholder="https://..."
                  value={badgeIconUrl}
                  onChange={(e) => setBadgeIconUrl(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 pb-8">
            <div>
              {isEditMode && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate("/admin/reading-rewards")}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                <Save className="h-4 w-4 mr-1" />
                {isSaving ? "Saving..." : isEditMode ? "Save Changes" : "Create Milestone"}
              </Button>
            </div>
          </div>
        </TabsContent>

        {isEditMode && (
          <TabsContent value="achievers" className="mt-4">
            <MilestoneAchieversTable milestoneId={Number(id)} />
          </TabsContent>
        )}
      </Tabs>

      {/* Delete dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader className="space-y-1">
            <DialogTitle>Delete Milestone</DialogTitle>
            <DialogDescription className="text-xs">
              Are you sure you want to delete this milestone? All user
              achievement records will also be deleted. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-1 sm:gap-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => deleteMutation.mutate(Number(id))}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ReadingMilestoneForm;
